import { useEffect, useRef } from 'react';
import type { NextRouter } from 'next/router';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import type { ChatState, PendingAttachment } from '../../../../lib/use-chat';
import type { ExternalChannel } from '../../../../lib/external-channel';
import type { SendReferences } from '../../../../lib/send-references';

// Autosend the URL-provided prefill once and only once (chat handoffs: Pulse
// Edit, Task chat-about, Knowledge Edit). New-thread handoffs (`?new=1`) let
// send() lazy-create; existing-thread handoffs wait until useChat has the right
// conversation loaded. A home handoff can carry files too — wait until they're
// uploaded and send them WITH the text as one message.
export function useAutosend({
  shouldAutosend,
  prefill,
  handoffOutreachId,
  handoffTaskId,
  handoffContext,
  isNewConv,
  selectedId,
  prefillFromStore,
  consumePrefill,
  chat,
  router,
  basePath,
  pendingUploadFiles,
  uploadsInFlight,
  readyAttachments,
  pendingAttachments,
  setPendingAttachments,
  lockedChannel,
  beginSendScroll,
  waitingForRecipient,
}: {
  shouldAutosend: boolean;
  prefill: string | null;
  /**
   * The inbound outreach this prefill is REPLYING to, when the handoff came from the outreach thread
   * view. Sent as `references.outreachId` — the signal that tells the agent to relay the message with
   * the outreach tools rather than treat it as a request of its own. `references` is a Struct on the
   * wire, so this rides with no proto change (same as `mentionedAgentId`).
   */
  handoffOutreachId?: string | null;
  /**
   * The task card this prefill was opened from, sent as `references.taskId`. It is what lets the run
   * refuse to open a second task for the card the user is already looking at.
   */
  handoffTaskId?: string | null;
  /**
   * The handoff's HIDDEN context line, sent as the stream endpoint's top-level `context` — the same field
   * the phones fill (`AppRouter.createGoalContext` / `ChatContext.createGoalContext`). It is NOT a
   * reference: `references` is persisted onto the message row's metadata, while `context` is prompt
   * context for this one turn, which is what a "the Space you are creating in is <id>" line wants to be.
   */
  handoffContext?: string | null;
  isNewConv: boolean;
  selectedId: string | null;
  // Where the pending prefill came from: the ActiveConversation store
  // (openChatWithMessage) rather than the URL query params. Governs how it's
  // cleared (consume the store intent vs. strip the params) and how strictly it
  // dedups (a store prefill is never re-fired by a reload).
  prefillFromStore: boolean;
  consumePrefill: () => void;
  chat: ChatState;
  router: NextRouter;
  basePath: string;
  pendingUploadFiles: File[] | null;
  uploadsInFlight: boolean;
  readyAttachments: PendingAttachment[];
  pendingAttachments: PendingAttachment[];
  setPendingAttachments: React.Dispatch<React.SetStateAction<PendingAttachment[]>>;
  // The open conversation is WhatsApp/SMS-owned (read-only on web) — never
  // autosend into it; the server guard would 403 it anyway.
  lockedChannel: ExternalChannel | null;
  // Glide the just-sent bubble up to the top so it's actually in view (and the
  // reply streams beneath it) — same as the manual submit path. Without this the
  // optimistic user message is appended but never scrolled into view, so it only
  // appears once the reply's auto-stick scroll fires.
  beginSendScroll: () => void;
  // The caller's recipient hasn't resolved yet — the chat is held behind the loader and there's no
  // identity to send under, so a handoff prefill must WAIT rather than fire a send into the void. The
  // effect re-runs once this flips false, sending the prefill the moment the recipient is ready.
  waitingForRecipient: boolean;
}) {
  // One-shot per handoff; re-armed when the consumed handoff's params leave the
  // URL (the overlay never remounts, so a second CTA must re-arm this).
  const autosentRef = useRef(false);
  useEffect(() => {
    if (!shouldAutosend) autosentRef.current = false;
  }, [shouldAutosend]);

  useEffect(() => {
    if (autosentRef.current || !shouldAutosend || !prefill) return;
    // Hold until the recipient resolves — the chat is behind the loader and there's no identity to
    // send under; the effect re-fires when this flips false.
    if (waitingForRecipient) return;
    // Never autosend into a WhatsApp/SMS-owned (read-only) thread — the server
    // guard would 403 it anyway; the locked bar is already showing.
    if (lockedChannel) return;
    const readyForExisting = !isNewConv && chat.conversationId === selectedId && !chat.loading;
    const readyForNew = isNewConv && selectedId === null && chat.conversationId === null;
    if (!readyForExisting && !readyForNew) return;
    if ((pendingUploadFiles && pendingUploadFiles.length > 0) || uploadsInFlight) return;

    const clearHandoff = () => {
      if (prefillFromStore) {
        consumePrefill();
        return;
      }
      if (!selectedId) return;
      void router.replace(
        { pathname: '/', query: { conversation: selectedId } },
        `${basePath}/?conversation=${encodeURIComponent(selectedId)}`,
        { shallow: true },
      );
    };

    if (readyForExisting && !prefillFromStore) {
      const trimmed = prefill.trim();
      const alreadySent = chat.messages.some((m) => m.role === 'USER' && (m.content ?? '').trim() === trimmed);
      if (alreadySent) {
        autosentRef.current = true;
        clearHandoff();
        return;
      }
    }

    autosentRef.current = true;
    const sendable: MessageAttachment[] = readyAttachments.map((p) => ({
      fileName: p.attachment!.fileName,
      mimeType: p.attachment!.mimeType,
      url: p.attachment!.url,
      ...(p.attachment!.size !== undefined ? { size: p.attachment!.size } : {}),
    }));
    // The outreach rider, when this prefill is a reply to one. Typed as `SendReferences` rather than
    // built inline so the extra key is a documented rider, not an object the compiler has to be talked
    // out of rejecting.
    const references: SendReferences | undefined =
      handoffOutreachId || handoffTaskId
        ? { ...(handoffOutreachId ? { outreachId: handoffOutreachId } : {}), ...(handoffTaskId ? { taskId: handoffTaskId } : {}) }
        : undefined;
    chat.send(
      prefill,
      sendable.length > 0 ? sendable : undefined,
      references,
      undefined,
      undefined,
      handoffContext ?? undefined,
    );
    beginSendScroll();
    if (readyAttachments.length > 0) {
      pendingAttachments.forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
      setPendingAttachments([]);
    }
    // New-conversation handoffs clear in onConversationCreated (one transition,
    // once the thread id exists); existing threads clear now.
    if (!isNewConv) clearHandoff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutosend, prefill, handoffContext, isNewConv, selectedId, prefillFromStore, chat, router, basePath, pendingUploadFiles, uploadsInFlight, readyAttachments.length, lockedChannel, beginSendScroll, waitingForRecipient]);
}
